import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Scale, Landmark, TrendingUp, FileSearch, Building2, Eraser, Briefcase } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow, EASE } from "@/components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const MARQUEE_ITEMS = [
  "+138 Avg Score Increase", "$12.5M+ Capital Funded", "CROA Compliant", "Metro 2 Auditing",
  "Free 15-Min Audit Call", "FCRA · FDCPA · FACTA", "4,200+ Clients Restored",
];

const SERVICES = [
  { icon: Eraser, title: "Negative Item Removal", desc: "Collections, charge-offs, late payments, and bankruptcies challenged line by line until they fall." },
  { icon: FileSearch, title: "Metro 2 Dispute Engine", desc: "We audit furnishers against the bureaus' own data standard. Most can't prove compliance. That's leverage." },
  { icon: ShieldCheck, title: "Inquiry Wiping", desc: "Unauthorized hard inquiries identified and removed across all three bureaus." },
  { icon: Building2, title: "Business Credit Builder", desc: "Establish EIN-based tradelines and vendor accounts that never touch your personal file." },
  { icon: Landmark, title: "Funding Access", desc: "Once your file is clean, we match you to $25k to $250k in personal and business capital." },
];

const CASES = [
  { name: "Dana M.", city: "Atlanta, GA", before: 542, after: 711, months: 5, removed: "9 collections, 4 late payments" },
  { name: "Marcus T.", city: "Houston, TX", before: 588, after: 742, months: 7, removed: "Charge-off, 6 collections, 3 inquiries" },
  { name: "Priya S.", city: "Phoenix, AZ", before: 601, after: 768, months: 6, removed: "Repossession record, 11 late marks" },
];

const FAQS = [
  { q: "Is credit repair legal?", a: "Yes. The Fair Credit Reporting Act (FCRA) gives you the right to challenge any item on your report that is inaccurate, incomplete, or unverifiable. We operate fully within FCRA, FDCPA, and the Credit Repair Organizations Act (CROA)." },
  { q: "How long does it take to see results?", a: "Most clients see their first deletions within 30 to 45 days. Full restoration typically takes 4 to 8 months depending on file complexity, and we keep you updated at every step." },
  { q: "What is Metro 2 compliance auditing?", a: "Metro 2 is the data format furnishers must use when reporting to bureaus. We audit every negative item for Metro 2 violations, and items that can't be verified compliantly must be deleted by law." },
  { q: "Will disputing hurt my credit score?", a: "No. Filing disputes does not lower your score. When negative items are deleted, most clients see their scores rise, often within the first 30-45 days." },
  { q: "When can I access funding?", a: "Once your score crosses 720, our funding desk pre-qualifies you with our lender network for $25k to $250k+ in personal lines, 0% APR business cards, and term loans." },
];

function TiltScoreCard() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} style={{ perspective: 1000 }} className="relative">
      <motion.div
        data-testid="hero-score-card"
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 60, rotateZ: -4 }}
        animate={{ opacity: 1, y: 0, rotateZ: -2 }}
        transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
        className="score-aura relative bg-gradient-to-br from-elevated to-surface border border-white/10 rounded-2xl p-8 md:p-10 w-full max-w-md mx-auto shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        <div className="flex justify-between items-start mb-10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">Clear Path Client</p>
            <p className="font-serif text-xl text-white">Score Trajectory</p>
          </div>
          <TrendingUp className="w-6 h-6 text-emerald" />
        </div>
        <div className="flex items-end gap-4 mb-8">
          <span className="font-serif text-7xl md:text-8xl text-white leading-none tracking-tight" data-testid="hero-score-value">738</span>
          <span className="bg-emerald/10 text-emerald border border-emerald/30 px-3 py-1 rounded-full text-xs font-mono tracking-widest mb-3">+180 PTS</span>
        </div>
        <div className="space-y-3">
          {["Equifax 738", "Experian 731", "TransUnion 744"].map((b, i) => (
            <div key={b} className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 w-28">{b.split(" ")[0]}</span>
              <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-gold/60 to-emerald"
                  initial={{ width: 0 }}
                  animate={{ width: `${68 + i * 4}%` }}
                  transition={{ duration: 1.4, delay: 1 + i * 0.2, ease: EASE }}
                />
              </div>
              <span className="font-mono text-xs text-white w-8 text-right">{b.split(" ")[1]}</span>
            </div>
          ))}
        </div>
        <div className="absolute -top-4 -right-4 bg-gold text-obsidian font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-lg rotate-6 shadow-lg">
          Funding Unlocked
        </div>
      </motion.div>
    </div>
  );
}

function Marquee() {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div data-testid="editorial-marquee" className="border-y border-white/10 bg-surface/50 py-5 overflow-hidden">
      <div className="animate-marquee flex whitespace-nowrap gap-0 w-max">
        {items.map((item, i) => (
          <span key={i} className="flex items-center">
            <span className={`mx-8 text-sm tracking-wide ${i % 2 ? "font-serif italic text-slate-400" : "font-mono uppercase text-xs tracking-[0.25em] text-gold/80"}`}>
              {item}
            </span>
            <span className="text-gold/40 text-xs">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ScoreSimulator() {
  const [score, setScore] = useState(620);
  const capacity = Math.min(250000, Math.max(0, (score - 500) * 900));
  const apr = (21.5 - ((score - 520) / 260) * 15.6).toFixed(1);
  const months = Math.max(2, Math.ceil((760 - score) / 45));

  return (
    <section data-testid="score-simulator" className="py-24 md:py-32 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <Eyebrow>Interactive Simulator</Eyebrow>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4 mb-6">
            Drag the score. <span className="italic text-gold">Watch what unlocks.</span>
          </h2>
          <p className="text-slate-400 leading-relaxed mb-10 max-w-md">
            Every point on your credit score is buying power. Slide from where you are to where we'll take you,
            and see what changes.
          </p>
          <div className="space-y-3">
            <div className="flex justify-between font-mono text-xs uppercase tracking-widest text-slate-500">
              <span>520</span>
              <span data-testid="simulator-score-value" className="text-gold text-sm">{score}</span>
              <span>780</span>
            </div>
            <input
              type="range" min="520" max="780" step="5" value={score}
              data-testid="score-simulator-slider"
              onChange={(e) => setScore(Number(e.target.value))}
              className="gold-slider w-full"
              style={{ "--fill": `${((score - 520) / 260) * 100}%` }}
            />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: "Funding Capacity", value: `$${capacity.toLocaleString()}`, id: "sim-capacity" },
            { label: "Auto Loan APR", value: `${apr}%`, id: "sim-apr" },
            { label: "Est. Repair Time", value: `${months} mo`, id: "sim-timeline" },
          ].map((s) => (
            <div key={s.id} data-testid={s.id} className="bg-surface border border-white/10 rounded-2xl p-6 text-center hover:border-gold/40 transition-colors duration-300">
              <p className="font-serif text-2xl md:text-3xl text-white mb-2">{s.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden py-28 md:py-36 px-6 lg:px-12">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(93,123,255,0.07),transparent_55%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:72px_72px]" />
        <div className="max-w-7xl mx-auto relative grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
              <Eyebrow className="mb-6">Credit Repair · Tampa, FL & Washington, DC</Eyebrow>
            </motion.div>
            <MaskedLines
              lines={["Good people.", "Bad credit.", "A clear path forward."]}
              className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.08] text-white"
              lineClassName=""
              delay={0.1}
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-8 text-slate-400 text-base md:text-lg leading-relaxed max-w-lg"
            >
              We don't beg the bureaus. We audit them with Metro 2 compliance precision until every
              inaccurate item is gone. Then we bridge you to the funding your new score deserves.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/pricing" data-testid="hero-cta-view-pricing" className="bg-gold text-obsidian hover:bg-gold-light hover:scale-[1.02] active:scale-[0.98] font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 rounded-xl transition-all duration-200 shadow-[0_0_24px_rgba(93,123,255,0.25)] flex items-center gap-2">
                View Pricing <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" data-testid="hero-cta-book-call" className="bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-white/30 font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 rounded-xl transition-all duration-200">
                Book a Free Call
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="mt-12 flex gap-10"
            >
              {[["4,200+", "Files Restored"], ["+138", "Avg. Points"], ["93%", "Deletion Rate"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-serif text-2xl md:text-3xl text-white">{v}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <TiltScoreCard />
        </div>
      </section>

      <Marquee />

      <ScoreSimulator />

      {/* MANIFESTO CHAPTERS */}
      <section data-testid="manifesto-section" className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <Eyebrow>The Clear Path Doctrine</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4 mb-16">
              Three chapters. <span className="italic text-gold">One outcome.</span>
            </h2>
          </Reveal>
          <div className="space-y-0">
            {[
              { n: "01", icon: Scale, title: "The Flawed Bureau System", body: "79% of credit reports contain errors. The bureaus profit from your low score because lenders pay more for risk. We start from a simple position: the burden of proof is on them, not you." },
              { n: "02", icon: FileSearch, title: "Legal Metro 2 Compliance Auditing", body: "Every negative item must be reported in the Metro 2 format, accurately and verifiably. Our auditors dissect each line of data. When furnishers can't prove compliance, and most can't, deletion is the law, not a favor." },
              { n: "03", icon: Landmark, title: "The Capital Catalyst Bridge", body: "A clean file is not the finish line. It's the starting line. Once your score crosses 720, our funding desk matches you to $25k to $250k+ in personal and business capital through our lender network." },
            ].map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 py-12 border-t border-white/10 hover:bg-white/[0.02] transition-colors duration-300 px-4 md:px-8 -mx-4 md:-mx-8 rounded-xl" data-testid={`manifesto-chapter-${c.n}`}>
                  <span className="md:col-span-2 font-serif text-5xl md:text-6xl text-white/10 group-hover:text-gold/40 transition-colors duration-500">{c.n}</span>
                  <div className="md:col-span-4">
                    <c.icon className="w-6 h-6 text-gold mb-4" />
                    <h3 className="font-serif text-xl md:text-2xl text-white tracking-tight">{c.title}</h3>
                  </div>
                  <p className="md:col-span-6 text-slate-400 leading-relaxed self-center">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section data-testid="services-section" className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5 bg-surface/30">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-16">
            <Eyebrow>Core Services</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4">
              Precision instruments, <span className="italic text-gold">not empty promises.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className={i < 2 ? "md:col-span-6" : "md:col-span-4"}>
                <div data-testid={`service-card-${i}`} className="group h-full bg-[#101218] border border-white/10 rounded-2xl p-8 md:p-10 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                    <s.icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl text-white mb-3 tracking-tight">{s.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section data-testid="cases-section" className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <Eyebrow>Verified Outcomes</Eyebrow>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4">
                Before <span className="text-slate-600 line-through decoration-red-500/50">→</span> <span className="italic text-emerald">After.</span>
              </h2>
            </div>
            <p className="text-slate-500 text-sm font-mono uppercase tracking-widest">Real clients · shared with permission</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CASES.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.1}>
                <div data-testid={`case-study-${i}`} className="bg-[#101218] border border-white/10 rounded-2xl p-8 hover:border-emerald/40 transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <p className="font-serif text-lg text-white">{c.name}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{c.city}</p>
                    </div>
                    <span className="bg-gold/10 text-gold border border-gold/30 px-3 py-1 rounded-full text-xs font-mono tracking-widest">{c.months} MO</span>
                  </div>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-serif text-4xl text-slate-600 line-through decoration-red-500/40">{c.before}</span>
                    <ArrowUpRight className="w-5 h-5 text-emerald" />
                    <span className="font-serif text-5xl text-emerald">{c.after}</span>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed border-t border-white/5 pt-5">Removed: {c.removed}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5 bg-surface/30">
        <div className="max-w-3xl mx-auto">
          <Reveal className="mb-14 text-center">
            <Eyebrow>Straight Answers</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4">Questions, answered.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-[#101218] border border-white/10 rounded-xl px-6 data-[state=open]:border-gold/30 transition-colors" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-serif text-base md:text-lg text-white hover:text-gold hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400 text-sm leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(93,123,255,0.08),transparent_60%)]" />
        <Reveal className="max-w-4xl mx-auto text-center relative">
          <Eyebrow className="mb-6">Free 15-Minute Audit Call</Eyebrow>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.08] text-white">
            Your score is not <br /><span className="italic text-gold">your verdict.</span>
          </h2>
          <p className="mt-8 text-slate-400 max-w-xl mx-auto leading-relaxed">
            Talk to a senior credit architect. We'll pull your file apart on the call, tell you exactly
            what's holding you down, and map the path out. Free.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-book-call-bottom" className="bg-gold text-obsidian hover:bg-gold-light hover:scale-[1.02] active:scale-[0.98] font-mono text-xs uppercase tracking-[0.15em] px-10 py-4 rounded-xl transition-all duration-200 shadow-[0_0_24px_rgba(93,123,255,0.25)]">
              Book My Free Call
            </Link>
            <Link to="/funding" data-testid="cta-funding-bottom" className="bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-white/30 font-mono text-xs uppercase tracking-[0.15em] px-10 py-4 rounded-xl transition-all duration-200">
              Explore Funding
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
